// Merchant console. KYB pill in the top-right, 4 stat tiles, quote
// inbox + active fulfillments + payout preview.
//
// Demo viewer: Marché Demo Léogâne (status=approved, KYB skipped).
// A real merchant who registered through /register?role=merchant would
// see status=applied/kyb_review and a banner blocking the inbox until
// approved — that branch lands in Phase 2 with the KYB workflow.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Inbox, Truck, Wallet, TrendingUp, ShoppingBag, Banknote, FileText, ShieldCheck, BadgeCheck, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { PortalShell } from '../components/shared/PortalShell';
import {
  Card, SectionHeader, StatTile, ActivityRow, StatusPill } from '../components/shared/primitives';
import { certificates, merchantStats, type CertLang } from '../lib/api';
import { formatHtgMinor } from '../lib/money';
import { MERCHANT_NAV } from './_navs';
import { useMerchantNav } from '../lib/pendingAction';


interface MerchantProfile { business_name?: string; status?: string; merchant_code?: string; city?: { name: string } }

function MerchantCertCard({ code }: { code?: string }) {
  const [lang, setLang] = useState<CertLang>('en');
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try { await certificates.mine(lang); }
    catch (e) { toast.error((e as Error).message || 'Download failed'); }
    finally { setBusy(false); }
  };
  return (
    <Card className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="inline-flex w-10 h-10 items-center justify-center rounded-xl bg-[var(--color-success-bg)] text-[var(--color-success-fg)] shrink-0"><BadgeCheck className="w-5 h-5" /></span>
        <div>
          <p className="text-[13.5px] font-semibold text-[var(--color-ink)]">Approved merchant</p>
          <p className="text-[12px] text-[var(--color-secondary-2)]">Merchant ID <span className="font-mono text-[var(--color-ink)]">{code ?? '—'}</span></p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <select value={lang} onChange={(e) => setLang(e.target.value as CertLang)} className="text-[12px] border border-[var(--color-border)] rounded-md px-2 py-1.5 bg-white" aria-label="Certificate language">
          <option value="en">English</option><option value="fr">Français</option><option value="ht">Kreyòl</option>
        </select>
        <button onClick={dl} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-600 text-white text-[12.5px] font-semibold hover:bg-brand-700 disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Certificate
        </button>
      </div>
    </Card>
  );
}

export function MerchantDashboard() {
  const { user } = useAuth();
  const mp = (user?.merchant_profile ?? {}) as MerchantProfile;
  const status = mp.status ?? 'applied';
  const approved = status === 'approved';

  // Badge the Home tab when this role owes an action, so a phone user
  // sees it from any screen rather than only on the right page.
  const nav = useMerchantNav(MERCHANT_NAV);

  return (
    <PortalShell
      nav={nav}
      activeId="dashboard"
      title={mp.business_name ?? 'Merchant'}
      subtitle="Quote requests, active orders, and payouts."
      actions={
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[var(--color-panel)] text-[11px] font-semibold text-[var(--color-body)]">
          <ShieldCheck className={`w-3.5 h-3.5 ${approved ? 'text-[var(--color-success-fg)]' : 'text-[var(--color-warning-fg)]'}`} />
          KYB <StatusPill status={status} />
        </span>
      }
    >
      <div className="space-y-6">
        {!approved && (
          <Card className="border-[var(--color-warning-border)] bg-[var(--color-warning-bg)]">
            <p className="text-[13px] font-semibold text-[var(--color-warning-fg)]">Application under review</p>
            <p className="mt-1 text-[12px] text-[var(--color-warning-fg)]">
              Your KYB documents are being reviewed. You'll be able to receive quote requests once approved (typically 1–3 business days).
            </p>
          </Card>
        )}

        {approved && <MerchantCertCard code={mp.merchant_code} />}

        <MerchantStats />
        <RunningLow />

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Quote inbox */}
          <div className="lg:col-span-2 space-y-5">
            <Card>
              <SectionHeader
                title="Quote requests"
                subtitle="Customers waiting for your price. Quotes expire after 24h."
                actions={<Link to="/merchant/quote-requests" className="text-[12px] font-semibold text-brand-600 hover:underline">View all →</Link>}
              />
              <MerchantQuoteInbox />
            </Card>

            <Card>
              <SectionHeader
                title="Active orders"
                subtitle="Paid orders to prepare and deliver."
                actions={<Link to="/merchant/orders" className="text-[12px] font-semibold text-brand-600 hover:underline">View all →</Link>}
              />
              <MerchantActiveOrders />
            </Card>
          </div>

          {/* Right column: payouts + activity */}
          <div className="space-y-5">
            <Card>
              <SectionHeader
                title="Payouts"
                actions={<Link to="/merchant/payouts" className="text-[12px] font-semibold text-brand-600 hover:underline">All →</Link>}
              />
              <div className="space-y-3">
                <NextPayoutCard />
                {/* Real payout history lives on /merchant/payouts, which reads
                    the ledger. This card previously listed three invented
                    payments — money the merchant never received, on the screen
                    they check to see whether they have been paid. */}
                <p className="py-6 text-center text-[12.5px] text-[var(--color-secondary-2)]">
                  Your payout history is on the Payouts page.
                </p>
              </div>
            </Card>

            <Card>
              <SectionHeader title="Activity" subtitle="Your most recent orders." />
              <MerchantActivity />
            </Card>
          </div>
        </div>

        <p className="text-[11px] text-[var(--color-muted)] inline-flex items-center gap-1">
          <FileText className="w-3 h-3" /> All numbers above are demo placeholders. Real data populates when Quote + Fulfillment controllers land in Phase 2.
        </p>
      </div>
    </PortalShell>
  );
}


/**
 * The four headline numbers, from /merchant/stats/overview.
 *
 * Every one of these used to be a literal. "HTG 9,840 pending payout" on the
 * screen a merchant opens to see whether they have been paid is the single
 * worst place on the platform for an invented number.
 */
/** "18 min" beats a timestamp when the question is how long someone waited. */
/**
 * Recent orders, from the merchant's own data.
 *
 * This card previously listed three literal rows — "Maria confirmed delivery
 * of REQ-1037", "REQ-1024 marked out for delivery" — naming customers and
 * orders that do not exist. Same class of invention as the payout history
 * removed in 6fac175; it survived because it was JSX rather than a number.
 */
/** Requests waiting on this merchant's price. */
/**
 * Items to restock, from the merchant's own stock states.
 *
 * The design asks for "Running low · Cooking oil · 6 left". We do not track a
 * quantity, so the count is not shown — `low` is the merchant's own judgement
 * and saying "6 left" would be inventing one. The prompt still works: these
 * are the items they flagged.
 */
function RunningLow() {
  const q = useQuery({ queryKey: ['merchant', 'overview'], queryFn: merchantStats.overview });
  const items = q.data?.unavailable_items ?? [];

  if (items.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[var(--color-warning-border)] bg-[var(--color-warning-bg)] px-4 py-3">
      <span className="text-[12px] font-extrabold uppercase tracking-wider text-[var(--color-warning-fg)]">Running low</span>
      {items.map((i) => (
        <span
          key={i.id}
          className={`rounded-md px-2 py-0.5 text-[12px] font-semibold ${
            i.stock_state === 'out'
              ? 'bg-[var(--color-danger-bg)] text-[var(--color-danger-fg)]'
              : 'bg-white text-[var(--color-warning-fg)]'
          }`}
        >
          {i.name}{i.stock_state === 'out' ? ' · out of stock' : ''}
        </span>
      ))}
      <Link
        to="/merchant/inventory"
        className="ml-auto rounded-lg bg-[var(--color-ink)] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[var(--color-primary)]"
      >
        Update inventory
      </Link>
    </div>
  );
}

function MerchantQuoteInbox() {
  const q = useQuery({ queryKey: ['merchant', 'overview'], queryFn: merchantStats.overview });
  const rows = q.data?.quote_requests ?? [];

  if (q.isLoading) {
    return <div className="py-6 text-center"><Loader2 className="inline h-4 w-4 animate-spin text-[var(--color-muted)]" aria-hidden="true" /></div>;
  }
  if (rows.length === 0) {
    return <p className="py-6 text-center text-[12.5px] text-[var(--color-secondary-2)]">No quote requests waiting.</p>;
  }

  return (
    <div className="-mx-1 divide-y divide-[var(--color-hairline)] px-1">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center gap-3 py-3">
          <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <ShoppingBag className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-[var(--color-ink)]">
              <span className="font-mono">{r.reference}</span>
              {r.recipient ? ` · ${r.recipient}` : ''}
            </p>
            <p className="truncate text-[11.5px] text-[var(--color-secondary-2)]">
              {r.item_count} item{r.item_count === 1 ? '' : 's'}
              {r.subtotal_htg_minor > 0 ? ` · ${formatHtgMinor(r.subtotal_htg_minor)}` : ''}
              {' · '}{relativeTime(r.created_at)} ago
            </p>
          </div>
          <Link
            to="/merchant/quote-requests"
            className="flex-none rounded-lg bg-brand-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-brand-700"
          >
            Send quote
          </Link>
        </div>
      ))}
    </div>
  );
}

/** Orders this merchant has to move. */
function MerchantActiveOrders() {
  const q = useQuery({ queryKey: ['merchant', 'overview'], queryFn: merchantStats.overview });
  const rows = q.data?.todays_orders ?? [];

  if (q.isLoading) {
    return <div className="py-6 text-center"><Loader2 className="inline h-4 w-4 animate-spin text-[var(--color-muted)]" aria-hidden="true" /></div>;
  }
  if (rows.length === 0) {
    return <p className="py-6 text-center text-[12.5px] text-[var(--color-secondary-2)]">Nothing to prepare right now.</p>;
  }

  return (
    <div className="-mx-1 divide-y divide-[var(--color-hairline)] px-1">
      {rows.map((o) => (
        <div key={o.request_id} className="flex items-center gap-3 py-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-[var(--color-ink)]">
              <span className="font-mono">{o.reference}</span>
              {o.recipient ? ` · ${o.recipient}` : ''}
            </p>
          </div>
          <StatusPill status={o.status} />
        </div>
      ))}
    </div>
  );
}

function MerchantActivity() {
  const q = useQuery({ queryKey: ['merchant', 'overview'], queryFn: merchantStats.overview });
  const rows = q.data?.todays_orders ?? [];

  if (q.isLoading) {
    return <div className="py-6 text-center"><Loader2 className="inline h-4 w-4 animate-spin text-[var(--color-muted)]" aria-hidden="true" /></div>;
  }

  if (rows.length === 0) {
    return <p className="py-6 text-center text-[12.5px] text-[var(--color-secondary-2)]">Nothing in progress right now.</p>;
  }

  return (
    <div className="-mx-1 divide-y divide-[var(--color-hairline)] px-1">
      {rows.map((o) => (
        <ActivityRow
          key={o.request_id}
          icon={Truck}
          iconTone="brand"
          title={<><strong>{o.reference}</strong>{o.recipient ? ` · ${o.recipient}` : ''}</>}
          meta={o.status.replace(/_/g, ' ')}
        />
      ))}
    </div>
  );
}

function relativeTime(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h`;
  return `${Math.round(hrs / 24)} d`;
}

function MerchantStats() {
  const q = useQuery({
    queryKey: ['merchant', 'overview'],
    queryFn: merchantStats.overview,
    refetchInterval: 60_000,
  });

  if (q.isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-[92px] animate-pulse rounded-xl border border-[var(--color-hairline)] bg-white" />
        ))}
      </div>
    );
  }

  const s = q.data?.stats;
  if (!s) return null;

  const waitingSince = s.oldest_awaiting_at
    ? `Oldest ${relativeTime(s.oldest_awaiting_at)}`
    : 'Nothing waiting';

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <StatTile icon={Inbox} tone="brand" label="New quote requests"
        value={String(s.awaiting_quote)} hint={waitingSince} />
      <StatTile icon={Truck} tone="amber" label="Orders to prepare"
        value={String(s.to_prepare)}
        hint={s.out_for_delivery > 0 ? `${s.out_for_delivery} out for delivery` : 'None out for delivery'} />
      <StatTile icon={Wallet} tone="default" label="Available payout"
        value={s.available_payout_htg_minor > 0 ? formatHtgMinor(s.available_payout_htg_minor) : '—'}
        hint="Confirmed deliveries only" />
      <StatTile icon={TrendingUp} tone="emerald" label="Median fulfillment"
        value={s.median_fulfillment_hours !== null ? `${s.median_fulfillment_hours} h` : '—'}
        hint={s.median_fulfillment_hours !== null ? 'Last 30 days' : 'Needs 3+ deliveries'} />
    </div>
  );
}

/** The one payout figure worth surfacing here: what is releasable now. */
function NextPayoutCard() {
  const q = useQuery({ queryKey: ['merchant', 'overview'], queryFn: merchantStats.overview });
  const amount = q.data?.stats.available_payout_htg_minor ?? 0;

  return (
    <div className="flex items-center justify-between rounded-lg border border-[var(--color-hairline)] bg-[var(--color-panel)] p-3">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-secondary-2)]">Available now</p>
        <p className="mt-0.5 font-mono text-[18px] font-semibold text-[var(--color-ink)]">
          {amount > 0 ? formatHtgMinor(amount) : '—'}
        </p>
        <p className="text-[11px] text-[var(--color-secondary-2)]">
          {amount > 0 ? 'Released after recipient confirmation' : 'Nothing ready to release'}
        </p>
      </div>
      <Banknote className="h-7 w-7 text-[var(--color-success-fg)]" aria-hidden="true" />
    </div>
  );
}

